import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Check, X, Eye, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [draftPosts, setDraftPosts] = useState([]);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDrafts, setSelectedDrafts] = useState([]);
  const [selectedPublished, setSelectedPublished] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [newFeed, setNewFeed] = useState({ name: '', url: '' });
  const [autoRssEnabled, setAutoRssEnabled] = useState(true);
  const [editingFeed, setEditingFeed] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', url: '' });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);

        if (userRes.data.email !== 'fablucvi@gmail.com') {
          alert('Acesso negado. Apenas o administrador pode acessar esta página.');
          navigate('/');
          return;
        }

        const [draftsRes, publishedRes] = await Promise.all([
          axios.get(`${API}/posts?status=draft&limit=100`, { withCredentials: true }),
          axios.get(`${API}/posts?limit=100`, { withCredentials: true })
        ]);

        setDraftPosts(draftsRes.data);
        setPublishedPosts(publishedRes.data);
        
        // Buscar feeds RSS e configurações
        const [feedsRes, configRes] = await Promise.all([
          axios.get(`${API}/rss-feeds`, { withCredentials: true }),
          axios.get(`${API}/config/auto_rss_enabled`, { withCredentials: true })
        ]);
        
        setFeeds(feedsRes.data);
        setAutoRssEnabled(configRes.data.value);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleApprove = async (postId) => {
    try {
      await axios.patch(`${API}/posts/${postId}/approve`, {}, { withCredentials: true });
      const post = draftPosts.find(p => p.post_id === postId);
      setDraftPosts(draftPosts.filter(p => p.post_id !== postId));
      setPublishedPosts([{ ...post, status: 'published' }, ...publishedPosts]);
      setSelectedDrafts(selectedDrafts.filter(id => id !== postId));
    } catch (error) {
      console.error('Error approving post:', error);
      alert('Erro ao aprovar post');
    }
  };

  const handleReject = async (postId) => {
    if (!window.confirm('Deseja rejeitar e deletar este post?')) return;
    
    try {
      await axios.patch(`${API}/posts/${postId}/reject`, {}, { withCredentials: true });
      setDraftPosts(draftPosts.filter(p => p.post_id !== postId));
      setSelectedDrafts(selectedDrafts.filter(id => id !== postId));
    } catch (error) {
      console.error('Error rejecting post:', error);
      alert('Erro ao rejeitar post');
    }
  };

  const handleDeletePublished = async (postId) => {
    if (!window.confirm('Deseja deletar este post publicado?')) return;
    
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      setPublishedPosts(publishedPosts.filter(p => p.post_id !== postId));
      setSelectedPublished(selectedPublished.filter(id => id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Erro ao deletar post');
    }
  };

  const handleApproveSelected = async () => {
    if (selectedDrafts.length === 0) return;
    if (!window.confirm(`Aprovar ${selectedDrafts.length} post(s)?`)) return;

    try {
      await Promise.all(
        selectedDrafts.map(postId =>
          axios.patch(`${API}/posts/${postId}/approve`, {}, { withCredentials: true })
        )
      );
      
      const approvedPosts = draftPosts.filter(p => selectedDrafts.includes(p.post_id));
      setDraftPosts(draftPosts.filter(p => !selectedDrafts.includes(p.post_id)));
      setPublishedPosts([...approvedPosts.map(p => ({ ...p, status: 'published' })), ...publishedPosts]);
      setSelectedDrafts([]);
      alert('Posts aprovados com sucesso!');
    } catch (error) {
      console.error('Error:', error);
      alert('Erro ao aprovar alguns posts');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedPublished.length === 0) return;
    if (!window.confirm(`Deletar ${selectedPublished.length} post(s) publicado(s)?`)) return;

    try {
      await Promise.all(
        selectedPublished.map(postId =>
          axios.delete(`${API}/posts/${postId}`, { withCredentials: true })
        )
      );
      
      setPublishedPosts(publishedPosts.filter(p => !selectedPublished.includes(p.post_id)));
      setSelectedPublished([]);
      alert('Posts deletados com sucesso!');
    } catch (error) {
      console.error('Error:', error);
      alert('Erro ao deletar alguns posts');
    }
  };

  const handleToggleAutoRss = async () => {
    try {
      const newValue = !autoRssEnabled;
      await axios.post(`${API}/config/auto_rss_enabled`, 
        { value: newValue }, 
        { withCredentials: true }
      );
      setAutoRssEnabled(newValue);
      alert(newValue ? 'Atualização automática ATIVADA' : 'Atualização automática DESATIVADA');
    } catch (error) {
      console.error('Error:', error);
      alert('Erro ao alterar configuração');
    }
  };

  const handleAddFeed = async (e) => {
    e.preventDefault();
    if (!newFeed.name || !newFeed.url) return;

    try {
      const response = await axios.post(`${API}/rss-feeds`, newFeed, { withCredentials: true });
      setFeeds([...feeds, response.data]);
      setNewFeed({ name: '', url: '' });
      alert('Feed RSS adicionado com sucesso!');
    } catch (error) {
      console.error('Error:', error);
      alert('Erro ao adicionar feed RSS. Verifique a URL.');
    }
  };

  const handleEditFeed = (feed) => {
    setEditingFeed(feed.feed_id);
    setEditForm({ name: feed.name, url: feed.url });
  };

  const handleUpdateFeed = async (feedId) => {
    if (!editForm.name || !editForm.url) return;

    try {
      const response = await axios.put(`${API}/rss-feeds/${feedId}`, editForm, { withCredentials: true });
      setFeeds(feeds.map(f => f.feed_id === feedId ? response.data : f));
      setEditingFeed(null);
      setEditForm({ name: '', url: '' });
      alert('Feed atualizado com sucesso!');
    } catch (error) {
      console.error('Error:', error);
      alert('Erro ao atualizar feed. Verifique a URL.');
    }
  };

  const handleDeleteFeed = async (feedId) => {
    if (!window.confirm('Deseja deletar este feed RSS?')) return;

    try {
      await axios.delete(`${API}/rss-feeds/${feedId}`, { withCredentials: true });
      setFeeds(feeds.filter(f => f.feed_id !== feedId));
    } catch (error) {
      console.error('Error:', error);
      alert('Erro ao deletar feed');
    }
  };

  const handleRefreshFeeds = async () => {
    try {
      const response = await axios.post(`${API}/rss-feeds/refresh`, {}, { withCredentials: true });
      alert(response.data.message);
      // Recarregar posts pendentes
      const draftsRes = await axios.get(`${API}/posts?status=draft&limit=100`, { withCredentials: true });
      setDraftPosts(draftsRes.data);
    } catch (error) {
      console.error('Error:', error);
      alert('Erro ao atualizar feeds');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Painel de Administração
          </h1>
          <p className="text-lg text-stone-700">
            Gerencie posts RSS pendentes e posts publicados
          </p>
        </div>

        {/* Configurações e Feeds RSS */}
        <div className="mb-12 bg-white rounded-lg border border-stone-200 p-6">
          <h2 className="text-2xl font-heading font-semibold mb-6">Configurações e Feeds RSS</h2>
          
          {/* Toggle Atualização Automática */}
          <div className="mb-6 p-4 bg-stone-50 rounded-md border border-stone-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-foreground">Atualização Automática de RSS</h3>
                <p className="text-sm text-stone-600 mt-1">
                  {autoRssEnabled 
                    ? 'Posts RSS são buscados automaticamente a cada 1 hora' 
                    : 'Atualização automática desativada - use refresh manual'}
                </p>
              </div>
              <button
                onClick={handleToggleAutoRss}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  autoRssEnabled ? 'bg-primary' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoRssEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Gerenciar Feeds RSS */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-foreground">Feeds RSS ({feeds.length})</h3>
              <Button
                onClick={handleRefreshFeeds}
                size="sm"
                variant="outline"
                className="rounded-md"
              >
                <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Buscar Agora
              </Button>
            </div>

            <form onSubmit={handleAddFeed} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <input
                type="text"
                placeholder="Nome do feed"
                value={newFeed.name}
                onChange={(e) => setNewFeed({ ...newFeed, name: e.target.value })}
                className="px-3 py-2 border rounded-md"
              />
              <input
                type="url"
                placeholder="URL do feed RSS"
                value={newFeed.url}
                onChange={(e) => setNewFeed({ ...newFeed, url: e.target.value })}
                className="px-3 py-2 border rounded-md"
              />
              <Button type="submit" className="rounded-md">
                Adicionar Feed
              </Button>
            </form>

            <div className="space-y-2">
              {feeds.map((feed) => (
                <div key={feed.feed_id} className="p-3 bg-stone-50 rounded-md border border-stone-100">
                  {editingFeed === feed.feed_id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                      />
                      <input
                        type="url"
                        value={editForm.url}
                        onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                      />
                      <div className="flex gap-2">
                        <Button onClick={() => handleUpdateFeed(feed.feed_id)} size="sm">
                          Salvar
                        </Button>
                        <Button onClick={() => setEditingFeed(null)} size="sm" variant="ghost">
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{feed.name}</p>
                        <p className="text-xs text-stone-500 truncate">{feed.url}</p>
                      </div>
                      <div className="flex gap-2 ml-2">
                        <button
                          onClick={() => handleEditFeed(feed)}
                          className="text-stone-600 hover:text-primary"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteFeed(feed.feed_id)}
                          className="text-stone-600 hover:text-red-600"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Posts Pendentes (Rascunhos) */}
        <div className="mb-12 bg-white rounded-lg border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-semibold">
                Posts RSS Pendentes ({draftPosts.length})
              </h2>
              <p className="text-sm text-stone-600 mt-1">
                Aguardando aprovação para publicação
              </p>
            </div>
            {selectedDrafts.length > 0 && (
              <Button
                onClick={handleApproveSelected}
                className="rounded-md"
                data-testid="approve-selected-button"
              >
                <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Aprovar ({selectedDrafts.length})
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {draftPosts.length === 0 ? (
              <p className="text-stone-500 text-center py-8">Nenhum post pendente</p>
            ) : (
              draftPosts.map((post) => (
                <div
                  key={post.post_id}
                  className="p-4 bg-yellow-50 rounded-md border border-yellow-200"
                  data-testid={`draft-post-${post.post_id}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedDrafts.includes(post.post_id)}
                      onChange={() => setSelectedDrafts(prev =>
                        prev.includes(post.post_id)
                          ? prev.filter(id => id !== post.post_id)
                          : [...prev, post.post_id]
                      )}
                      className="w-4 h-4 text-primary rounded cursor-pointer mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">{post.title}</p>
                      <p className="text-sm text-stone-600 mt-1 line-clamp-2">
                        {post.content.replace(/<[^>]*>/g, '')}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                        <span>Fonte: {post.source_name}</span>
                        <span>
                          {formatDistanceToNow(new Date(post.created_at), {
                            addSuffix: true,
                            locale: ptBR
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/post/${post.post_id}`} target="_blank">
                        <Button variant="ghost" size="sm" className="rounded-md">
                          <Eye className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </Link>
                      <Button
                        onClick={() => handleApprove(post.post_id)}
                        size="sm"
                        className="rounded-md bg-green-600 hover:bg-green-700"
                      >
                        <Check className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        onClick={() => handleReject(post.post_id)}
                        size="sm"
                        variant="destructive"
                        className="rounded-md"
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Posts Publicados */}
        <div className="bg-white rounded-lg border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-semibold">
                Posts Publicados ({publishedPosts.length})
              </h2>
              <p className="text-sm text-stone-600 mt-1">
                Todos os posts visíveis no blog
              </p>
            </div>
            {selectedPublished.length > 0 && (
              <Button
                onClick={handleDeleteSelected}
                variant="destructive"
                size="sm"
                className="rounded-md"
              >
                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Deletar ({selectedPublished.length})
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {publishedPosts.slice(0, 50).map((post) => (
              <div
                key={post.post_id}
                className="p-4 bg-stone-50 rounded-md border border-stone-100"
                data-testid={`published-post-${post.post_id}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedPublished.includes(post.post_id)}
                    onChange={() => setSelectedPublished(prev =>
                      prev.includes(post.post_id)
                        ? prev.filter(id => id !== post.post_id)
                        : [...prev, post.post_id]
                    )}
                    className="w-4 h-4 text-primary rounded cursor-pointer mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">{post.title}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-stone-500">
                      {post.is_rss && <span className="px-2 py-0.5 bg-stone-200 rounded">RSS</span>}
                      {post.source_name && <span>{post.source_name}</span>}
                      <span>
                        {formatDistanceToNow(new Date(post.published_at), {
                          addSuffix: true,
                          locale: ptBR
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/post/${post.post_id}`} target="_blank">
                      <Button variant="ghost" size="sm" className="rounded-md">
                        <Eye className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </Link>
                    <Button
                      onClick={() => handleDeletePublished(post.post_id)}
                      variant="ghost"
                      size="sm"
                      className="rounded-md text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
